import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { DropDownOption } from '~types'

export interface UseDropdownProps<T extends DropDownOption = DropDownOption> {
  options?: T[]
  placeholder?: string
  onOptionSelect?: (value: string, option: T) => void
  includeClearOption?: boolean
  showPlaceholderInMenu?: boolean
  initialSelectedValue?: string
}

export const useDropdown = <T extends DropDownOption = DropDownOption>({
  options = [],
  placeholder = 'Select option',
  onOptionSelect,
  includeClearOption = true,
  showPlaceholderInMenu = true,
  initialSelectedValue = ''
}: UseDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [clearOptionAlreadySelected, setClearOptionAlreadySelected] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setSelectedValue(initialSelectedValue)
  }, [initialSelectedValue])

  useEffect(() => {
    const match = options.find(
      opt =>
        opt.label?.toLowerCase() === placeholder.toLowerCase() || opt.value?.toLowerCase() === placeholder.toLowerCase()
    )
    if (match) {
      setSelectedValue(match.value || '')
    }
  }, [options, placeholder])

  // Get all menu items including the clear option
  const getMenuItems = (): T[] => {
    const clearOption = { value: '', label: placeholder } as T

    if (!includeClearOption) return options

    // Only add placeholder as first menu item if required
    return showPlaceholderInMenu ? [clearOption, ...options] : options
  }

  const getSelectedLabel = (): string => {
    if (!selectedValue) return placeholder

    const selectedOption = options.find(option => option.value === selectedValue)
    return selectedOption ? selectedOption.label || selectedOption.value || 'Invalid option' : placeholder
  }

  const handleToggle = () => {
    setIsOpen(prev => !prev)
  }

  const handleOptionClick = (value: string) => {
    setSelectedValue(value)
    setIsOpen(false)

    if (value === '' && !clearOptionAlreadySelected) {
      setClearOptionAlreadySelected(true)
    }

    const selectedOption = getMenuItems().find(option => option.value === value)
    if (selectedOption && onOptionSelect) {
      onOptionSelect(value, selectedOption)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const menuItems = getMenuItems()

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        } else if (focusedIndex >= 0) {
          handleOptionClick(menuItems[focusedIndex].value ?? '')
        }
        break

      case 'Escape':
        setIsOpen(false)
        toggleButtonRef.current?.focus()
        break

      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        setFocusedIndex(prev => (prev < menuItems.length - 1 ? prev + 1 : 0))
        break

      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        setFocusedIndex(prev => (prev <= 0 ? menuItems.length - 1 : prev - 1))
        break

      case 'Home':
        event.preventDefault()
        setFocusedIndex(0)
        break

      case 'End':
        event.preventDefault()
        setFocusedIndex(menuItems.length - 1)
        break

      case 'Tab':
        if (isOpen) {
          setIsOpen(false)
        }
        break
    }
  }

  const handleOptionKeyDown = (event: KeyboardEvent<HTMLDivElement>, value: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOptionClick(value)
    }
  }

  const closeDropdown = () => {
    setIsOpen(false)
    setFocusedIndex(-1)
  }

  const clearSelection = () => {
    setSelectedValue('')
    setClearOptionAlreadySelected(false)
  }

  const resetDropdown = () => {
    clearSelection()
    closeDropdown()
  }

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1)
    }
  }, [isOpen])

  // Focus management for keyboard navigation
  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      const menuItems = dropdownRef.current?.querySelectorAll('[role="option"]')
      if (menuItems && menuItems[focusedIndex]) {
        ;(menuItems[focusedIndex] as HTMLElement).focus()
      }
    }
  }, [focusedIndex, isOpen])

  return {
    isOpen,
    selectedValue,
    focusedIndex,
    clearOptionAlreadySelected,
    dropdownRef,
    toggleButtonRef,
    menuItems: getMenuItems(),
    selectedLabel: getSelectedLabel(),
    handleToggle,
    handleOptionClick,
    handleKeyDown,
    handleOptionKeyDown,
    closeDropdown,
    setFocusedIndex,
    clearSelection,
    resetDropdown,
    getPlaceholderText: () => placeholder
  }
}
